import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCart, removeOrder } from '../../redux/menu/cartSlice';
import { toggleFavorite } from '../../redux/menu/favoritesSlice';
import { isProductAvailable, getNextAvailableTime, formatTimeForDisplay } from '../../utils/timeAvailability';
import useTheme from '../../hooks/useTheme';
import ContactInfo from '../Common/ContactInfo';
import { FaMapMarkerAlt, FaBed, FaBath, FaRulerCombined } from 'react-icons/fa';
import { HiOutlineHeart, HiHeart } from 'react-icons/hi';

const FoodItem = ({ image, name, description, price, id, currency, options, is_veg, variants, productOptions, sizes, ProductVariants, categoryName, categoryDescription, subcategoryName, showAddToCart = true, highlight = false, status, onViewDetails, profile, ...rest }) => {
    
    const theme = useTheme();
    
    // Format availability date - Mobile: "18 Dec 2002", Desktop: "March 6, 2027"
    const formatAvailabilityDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            // Check if mobile device
            const isMobile = window.innerWidth < 768;
            
            if (isMobile) {
                // Mobile format: "18 Dec 2002"
                const day = date.getDate();
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const month = monthNames[date.getMonth()];
                const year = date.getFullYear();
                return `${day} ${month} ${year}`;
            } else {
                // Desktop format: "March 6, 2027"
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        } catch (error) {
            return dateString;
        }
    };
    
    // Check if product is available based on time
    const isTimeAvailable = isProductAvailable(rest?.ProductCategory);
    const nextAvailableTime = getNextAvailableTime(rest?.ProductCategory);
    
    // Check if product status is active (status should not be false)
    const isStatusActive = status !== false;
    
    // Product is available only if both time and status are valid
    const isAvailable = isTimeAvailable && isStatusActive;
    
    const dispatch = useDispatch();
    const cartItems = useSelector((state) => state.cart.cart);
    const favorites = useSelector((state) => state.favorites.favorites);
    const [variantQuantities, setVariantQuantities] = useState({});

    const [showImageModal, setShowImageModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    // Get all product images including main image and additional images
    const getAllProductImages = () => {
        const images = [];
        
        // Add main product image
        if (image) {
            images.push(image);
        }
        
        // Add additional product images from rest object
        if (rest?.ProducImages && Array.isArray(rest.ProducImages)) {
            rest.ProducImages.forEach(img => {
                if (img.DocImage?.imageUrl) {
                    images.push(img.DocImage.imageUrl);
                }
            });
        }
        
        // Also check for productImages field (alternative naming)
        if (rest?.productImages && Array.isArray(rest.productImages)) {
            rest.productImages.forEach(img => {
                if (img.DocImage?.imageUrl) {
                    images.push(img.DocImage.imageUrl);
                } else if (typeof img === 'string') {
                    images.push(img);
                }
            });
        }
        
        return images;
    };
    
    const productImages = getAllProductImages();

    // Helper function to format price - remove .00 if decimal part is 00
    const formatPrice = (price) => {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
        const formattedPrice = numericPrice.toFixed(2);
        return formattedPrice.replace('.00', '');
    };
    
    // Memoize variant options to prevent infinite re-renders
    const finalVariantOptions = useMemo(() => {
        // Check for ProductVariants field first (your API field)
        let variantOptions = ProductVariants || [];
        
        // If no ProductVariants, check other possible field names
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = options || variants || productOptions || sizes || 
                            (rest?.variants) || (rest?.options) || (rest?.productOptions) || (rest?.sizes) || [];
        }
        
        // If variants is a string, try to parse it as JSON
        if (typeof variantOptions === 'string') {
            try {
                variantOptions = JSON.parse(variantOptions);
            } catch (e) {
                variantOptions = [];
            }
        }
        
        // Check for other common field names as fallback
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = rest?.product_variants || rest?.productVariants || rest?.item_variants || rest?.itemVariants || 
                            rest?.productOptions || rest?.product_options || rest?.variants || rest?.options || [];
        }
        
        return variantOptions || [];
    }, [ProductVariants, options, variants, productOptions, sizes, rest]);

    const finalHasVariants = useMemo(() => {
        return finalVariantOptions && finalVariantOptions.length > 0;
    }, [finalVariantOptions]);
    
    // Sync local state with cart state
    useEffect(() => {
        const syncQuantities = () => {
            const newQuantities = {};
            
            // For products with variants
            if (finalHasVariants) {
                finalVariantOptions.forEach(variant => {
                    const cartItem = cartItems.find(item => item.id === `${id}-${variant.id}`);
                    if (cartItem) {
                        newQuantities[variant.id] = cartItem.quantity;
                    }
                });
            } else {
                // For regular products without variants
                const cartItem = cartItems.find(item => item.id === id);
                if (cartItem) {
                    newQuantities['base'] = cartItem.quantity;
                }
            }
            
            // Only update state if quantities have actually changed
            setVariantQuantities(prevQuantities => {
                const hasChanged = JSON.stringify(prevQuantities) !== JSON.stringify(newQuantities);
                return hasChanged ? newQuantities : prevQuantities;
            });
        };
        
        syncQuantities();
    }, [cartItems, id, finalHasVariants, finalVariantOptions]);

    // Check if product is favorited
    const isFavorited = favorites.some(item => item.id === id);

    // Handle favorite toggle
    const handleFavoriteToggle = (e) => {
        e.stopPropagation(); // Prevent card click
        const productData = {
            id,
            name,
            description,
            price,
            currency,
            image,
            categoryName,
            subcategoryName,
            is_veg,
            ...rest
        };
        dispatch(toggleFavorite(productData));
    };
    
    // Get total quantity across all variants
    const getTotalQuantity = () => {
        return Object.values(variantQuantities).reduce((total, qty) => total + qty, 0);
    };
    
    // Get total price across all variants
    const getTotalPrice = () => {
        let total = 0;
        finalVariantOptions.forEach(variant => {
            const qty = variantQuantities[variant.id] || 0;
            const variantPrice = variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
            total += variantPrice * qty;
        });
        return total;
    };

    // Helper function to get the effective price (offer price if available, otherwise regular price)
    const getEffectivePrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
    };

    // Helper function to check if offer price is available
    const hasOfferPrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null && variant.offerPrice !== "";
    };


    const handleImageModalOpen = () => {
        setCurrentImageIndex(0);
        setShowImageModal(true);
    };


    const handlePreviousImage = () => {
        setCurrentImageIndex((prev) => 
            prev === 0 ? productImages.length - 1 : prev - 1
        );
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => 
            prev === productImages.length - 1 ? 0 : prev + 1
        );
    };

    const handleImageModalClose = () => {
        setShowImageModal(false);
        setCurrentImageIndex(0);
    };

    const handleMapOpen = (e) => {
        e.stopPropagation();
        if (rest?.map_url) {
            window.open(rest.map_url, '_blank');
        }
    };

    const handleVariantQuantityChange = (variantId, newQuantity) => {
        if (newQuantity >= 0) {
            const updatedQuantities = { ...variantQuantities };
            
            if (newQuantity > 0) {
                updatedQuantities[variantId] = newQuantity;
                setVariantQuantities(updatedQuantities);
                
                // Add to cart
                if (finalHasVariants) {
                    const variant = finalVariantOptions.find(v => v.id === variantId);
                    const effectivePrice = getEffectivePrice(variant);
                    const cartItem = {
                        id: `${id}-${variantId}`,
                        name: variant.name && variant.name.trim() !== '' ? `${name} (${variant.name})` : name,
                        price: effectivePrice,
                        originalPrice: parseFloat(variant.price),
                        offerPrice: variant.offerPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        variant: variant,
                        storeId: profile?.id // Add store ID
                    };
                    dispatch(addCart(cartItem));
                } else {
                    // Regular product without variants - check if product has offer price
                    const productOfferPrice = rest?.offerPrice;
                    const effectivePrice = productOfferPrice && productOfferPrice !== null ? parseFloat(productOfferPrice) : parseFloat(price);
                    const cartItem = {
                        id: id,
                        name: name,
                        price: effectivePrice,
                        originalPrice: parseFloat(price),
                        offerPrice: productOfferPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        storeId: profile?.id // Add store ID
                    };
                    dispatch(addCart(cartItem));
                }
            } else if (newQuantity === 0) {
                delete updatedQuantities[variantId];
                setVariantQuantities(updatedQuantities);
                
                // Remove from cart
                const cartId = finalHasVariants ? `${id}-${variantId}` : id;
                dispatch(removeOrder(cartId));
            }
        }
    };
    // Regular card layout
    if (!highlight) {
        return (
            <>
            <div 
                className={`bg-white border border-gray-100 transition transform duration-700 rounded-xl md:rounded-2xl relative min-h-[400px] sm:min-h-[440px] md:min-h-[480px] lg:min-h-[520px] xl:min-h-[560px] flex flex-col w-full shadow-lg md:shadow-xl ${
                    isAvailable 
                        ? 'hover:shadow-xl md:hover:shadow-2xl hover:scale-[1.02] md:hover:scale-105 cursor-pointer' 
                        : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => {
                    if (isAvailable && onViewDetails) {
                        onViewDetails(id);
                    }
                }}
            >
                
                
                {/* Badge Ribbon */}
                {rest?.badge && (
                    <div className="absolute top-0 right-0 z-30 w-28 h-20 overflow-hidden">
                        <div className="absolute -top-2 -right-2 text-white text-[4px] font-bold px-1 py-1 shadow-lg transform rotate-45" style={{
                            top : "20%",
                            left : "25%",
                            width : "100%",
                            fontSize : "10px",
                            textAlign : "center",
                            backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg
                        }}>
                            {rest.badge}
                        </div>
                    </div>
                )}
                
                {/* Availability Message */}
                {!isAvailable && (
                    <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-1 px-2 text-xs font-bold z-30">
                        {'Currently Unavailable'}
                        {isStatusActive && nextAvailableTime && (
                            <div className="text-xs opacity-90">
                                Available at {formatTimeForDisplay(nextAvailableTime)}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Main Content Area */}
                <div className="flex flex-row items-stretch p-0 flex-1">
                    {/* Product Image - Left Side */}
                    <div className="relative flex-shrink-0 mr-0">
                        <img 
                            className="w-48 h-44 sm:w-52 sm:h-48 md:w-60 md:h-56 lg:w-68 lg:h-64 xl:w-80 xl:h-76 transform transition duration-300 hover:scale-105 object-cover cursor-pointer rounded-none" 
                            src={`${image}`} 
                            alt={name}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleImageModalOpen();
                            }}
                        />
                        
                        {/* Location Icon - Only show if map_url exists */}
                        {rest?.map_url && (
                            <button
                                onClick={handleMapOpen}
                                className="absolute bottom-1 right-1 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors duration-200 z-10"
                                title="Open Map Location"
                            >
                                <FaMapMarkerAlt 
                                    className="w-4 h-4" 
                                    style={{ color: theme.colors.primary }}
                                />
                            </button>
                        )}
                        
                        {/* Seasonal Badge */}
                        {rest?.seasonal && (
                            <div style={{
                                position: 'absolute',
                                bottom: '-1px',
                                right: '-1px',
                                background: `linear-gradient(to right, ${theme.colors.seasonalBadgeFrom}, ${theme.colors.seasonalBadgeTo})`,
                                color: 'white',
                                fontSize: '7px',
                                fontWeight: 'bold',
                                padding: '2px 4px',
                                borderRadius: '12px 0px 0px 0px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                zIndex: 10
                            }}>
                                Special Edition
                            </div>
                        )}

                        {/* Favorites Button - Move to bottom-left of image (mobile smaller) */}
                        <button
                            onClick={handleFavoriteToggle}
                            className="absolute bottom-1 left-1 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
                            style={{
                                backgroundColor: isFavorited ? '#ec4899' : 'rgba(255, 255, 255, 0.9)',
                                color: isFavorited ? 'white' : theme.colors.primary,
                                border: `2px solid ${isFavorited ? '#ec4899' : 'rgba(0, 0, 0, 0.1)'}`
                            }}
                            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            {isFavorited ? <HiHeart className="text-lg md:text-xl" /> : <HiOutlineHeart className="text-lg md:text-xl" />}
                        </button>
                    </div>
                    
                    {/* Content Section - Right Side */}
                    <div className="flex-1 flex flex-col justify-between p-3 md:p-4 ">
                        {/* Top Content Container */}
                        <div className="flex flex-col flex-1">
                            {/* Product Name - Top of Content */}
                            <div className="mb-1 md:mb-2">
                                <h1 className="text-gray-900 poppins text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold leading-tight">{name}</h1>
                            </div>
                        
                        {/* Price - Below Product Name - Only show if no Payment Type specification */}
                        {!finalHasVariants && !rest?.ProductSpecifications?.some(spec => spec.key === "Payment Type") && (
                            <div className="mb-2 md:mb-3">
                                <div className={`text-gray-900 poppins text-xs sm:text-sm md:text-base font-semibold ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                    {rest?.offerPrice && rest?.offerPrice !== null ? (
                                        <div className="flex flex-col">
                                            <span className="font-semibold" style={{ color: theme.colors.offerPriceText }}>{currency} {(parseFloat(rest.offerPrice) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                            <span className="text-gray-500 line-through text-[10px]">{currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                        </div>
                                    ) : (
                                        <span>{currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Payment Type - Only show if exists */}
                        {rest?.ProductSpecifications && Array.isArray(rest.ProductSpecifications) && rest.ProductSpecifications.some(spec => spec.key === "Payment Type") && (
                            <div className="mb-2 md:mb-3">
                                {rest.ProductSpecifications.map((spec) => {
                                    if (spec.key === "Payment Type") {
                                        const displayPrice = parseFloat(price).toFixed(2).replace('.00', '');
                                        return (
                                            <div key={spec.id} className="mb-2 md:mb-3">
                                                <div className={`text-gray-900 poppins text-xs sm:text-sm md:text-base font-semibold ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                                    {rest?.offerPrice && rest?.offerPrice !== null ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold" style={{ color: theme.colors.offerPriceText }}>{currency} {parseFloat(rest.offerPrice).toFixed(2).replace('.00', '')}/{spec.value}</span>
                                                            <span className="text-gray-500 line-through text-[10px]">{currency} {displayPrice}/{spec.value}</span>
                                                        </div>
                                                    ) : (
                                                        <span>{currency} {displayPrice}/{spec.value}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}
                        
                        {/* Available From - Below Price */}
                        {rest?.availability && (
                            <div className="mb-2 md:mb-3">
                                <div className="inline-flex items-center rounded-lg px-3 py-1" 
                                     style={{ 
                                         backgroundColor: '#f1f5f9',
                                         border: `1px solid #e2e8f0`
                                     }}>
                                    <p className="text-xs font-semibold whitespace-nowrap" 
                                       style={{ color: '#64748b' }}>
                                        From: {formatAvailabilityDate(rest.availability)}
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {/* Features Section - Show first 5 features */}
                        {rest?.ProductFeatures && Array.isArray(rest.ProductFeatures) && rest.ProductFeatures.length > 0 && (
                            <div className="mb-2 md:mb-3">
                                <div className="text-gray-600 text-xs sm:text-sm">
                                    {rest.ProductFeatures.slice(0, 5).map((feature, index) => (
                                        <span key={feature.id || index}>
                                            {feature.name || feature.feature || feature}
                                            {index < Math.min(rest.ProductFeatures.length, 5) - 1 && ' | '}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        
                        {/* Product Details */}
                        <div className="flex flex-col space-y-1 md:space-y-2 flex-1">
                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="flex flex-col gap-2 md:gap-3 justify-center mb-2 md:mb-3 w-full">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-gray-50 rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm">
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-700 truncate">{variant.name}</div>
                                            <div className="text-sm font-bold" style={{ color: theme.colors.variantPriceText }}>
                                                {hasOfferPrice(variant) ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                        <span className="text-gray-500 line-through text-xs">{currency} {formatPrice(variant.price)}</span>
                                                    </div>
                                                ) : (
                                                    <span>{currency} {formatPrice(variant.price)}</span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty > 0 && (
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 md:w-7 md:h-7 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-base font-bold shadow-sm border border-gray-200 ${
                                                            isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-sm md:text-base font-bold text-gray-800 min-w-[20px] md:min-w-[25px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-base font-bold shadow-sm ${
                                                            isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                        style={isAvailable ? {
                                                            backgroundColor: theme.colors.productAddButtonBg,
                                                            color: theme.colors.productAddButtonText,
                                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                        } : {}}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center mt-1 md:mt-2">
                            <div className="text-xs text-gray-600">Total: {getTotalQuantity()} items</div>
                            <div className={`text-gray-900 poppins text-sm md:text-base font-bold ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {formatPrice(getTotalPrice())}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="flex flex-col items-center">
                            {showAddToCart && (
                                variantQuantities['base'] > 0 && (
                                    <div className="flex items-center space-x-3 md:space-x-4 bg-gray-50 rounded-full px-3 py-2 md:px-4 md:py-3 shadow-md">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            disabled={!isAvailable}
                                            className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-base md:text-lg font-bold shadow-sm border border-gray-200 ${
                                                isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                        >
                                            -
                                        </button>
                                        <span className="text-base sm:text-lg md:text-xl font-bold text-gray-800 min-w-[25px] sm:min-w-[30px] md:min-w-[35px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            disabled={!isAvailable}
                                            className={`w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-all duration-200 text-base md:text-lg font-bold shadow-sm ${
                                                isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                            style={isAvailable ? {
                                                backgroundColor: theme.colors.productAddButtonBg,
                                                color: theme.colors.productAddButtonText,
                                                border: `1px solid ${theme.colors.productAddButtonBorder}`
                                            } : {}}
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                        </div>
                        
                        {/* Location and Specifications - Bottom of Text Area */}
                        <div className="pt-2 md:pt-3">
                            {/* Location */}
                            {rest?.warranty && (
                                <div className="flex items-center gap-2 mb-1">
                                    <FaMapMarkerAlt 
                                        className="w-4 h-4" 
                                        style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                    />
                                    <span className="text-gray-500 text-[10px]">{rest.warranty}</span>
                                </div>
                            )}
                            
                            {/* Property Specifications */}
                            {(rest?.ProductSpecifications && Array.isArray(rest.ProductSpecifications) && 
                              (rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                               rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") || 
                               rest.ProductSpecifications.find(spec => spec.key === "Property Size"))) && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* Bedrooms */}
                                    {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && (
                                        <div className="flex items-center gap-1" title="Bedrooms">
                                            <FaBed 
                                                className="w-4 h-4" 
                                                style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                title="Bedrooms"
                                            />
                                            <span 
                                                className="text-gray-500 text-[10px]"
                                                title="Bedrooms"
                                            >
                                                {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms")?.value}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Bathrooms */}
                                    {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") && (
                                        <>
                                            {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && <span className="text-gray-400 text-[10px]">|</span>}
                                            <div className="flex items-center gap-1" title="Bathrooms">
                                                <FaBath 
                                                    className="w-4 h-4" 
                                                    style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                    title="Bathrooms"
                                                />
                                                <span 
                                                    className="text-gray-500 text-[10px]"
                                                    title="Bathrooms"
                                                >
                                                    {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")?.value}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    
                                    {/* Square Feet */}
                                    {rest.ProductSpecifications.find(spec => spec.key === "Property Size") && (
                                        <>
                                            {(rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                                              rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")) && <span className="text-gray-400 text-[10px]">|</span>}
                                            <div className="flex items-center gap-1" title="Square Feet">
                                                <FaRulerCombined 
                                                    className="w-4 h-4" 
                                                    style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                    title="Square Feet"
                                                />
                                                <span 
                                                    className="text-gray-500 text-[10px]"
                                                    title="Square Feet"
                                                >
                                                    {rest.ProductSpecifications.find(spec => spec.key === "Property Size")?.value}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
                
                {/* Favorites Button moved into image container above */}
                
                {/* Contact Information - Full Width Footer at Bottom */}
                <div className="w-full bg-gray-50 rounded-b-xl md:rounded-b-2xl p-2 md:p-3 mt-auto">
                    <ContactInfo 
                        profile={profile} 
                        isHighlighted={false} 
                        productDetails={{
                            name,
                            price,
                            currency,
                            description,
                            categoryName,
                            subcategoryName,
                            brand: rest?.brand,
                            model: rest?.model,
                            ProductSpecifications: rest?.ProductSpecifications,
                            warranty: rest?.warranty
                        }}
                    />
                </div>
            </div>
            
            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-1" onClick={handleImageModalClose}>
                    <div className="relative w-full bg-white rounded-lg overflow-hidden shadow-2xl" style={{ height: '80vh', maxWidth: '95vw', marginTop: '8vh', marginBottom: '12vh' }} onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={handleImageModalClose}
                            className="absolute top-4 right-4 z-10 bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-600 transition-all duration-200 shadow-lg border-2 border-white"
                            style={{ fontSize: '20px', fontWeight: 'bold' }}
                        >
                            ×
                        </button>
                        
                        {/* Navigation buttons - only show if multiple images */}
                        {productImages.length > 1 && (
                            <>
                                <button 
                                    onClick={handlePreviousImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                                >
                                    ‹
                                </button>
                                <button 
                                    onClick={handleNextImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                                >
                                    ›
                                </button>
                            </>
                        )}
                        
                        <img 
                            src={productImages[currentImageIndex]} 
                            alt={`${name} ${currentImageIndex + 1} of ${productImages.length}`}
                            className="w-full h-full object-contain"
                        />
                        
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                            <h3 className="text-white text-lg font-semibold">{name}</h3>
                        </div>
                        
                        {/* Image dots indicator - only show if multiple images */}
                        {productImages.length > 1 && (
                            <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-2 p-4">
                                {productImages.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentImageIndex(index)}
                                        className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                            index === currentImageIndex 
                                                ? 'bg-gray-800 scale-125' 
                                                : 'bg-gray-400 hover:bg-gray-600'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            </>
        )
    }

    // Highlighted card layout (spans 2 columns)
    return (
        <>
        <div 
            className={`col-span-2 border-2 border-primary/30 rounded-xl md:rounded-2xl relative overflow-hidden shadow-xl md:shadow-2xl transition-all duration-500 flex flex-col min-h-[440px] sm:min-h-[500px] md:min-h-[540px] lg:min-h-[600px] xl:min-h-[640px] ${
                isAvailable 
                    ? 'hover:shadow-2xl md:hover:shadow-3xl hover:scale-[1.01] md:hover:scale-[1.02] cursor-pointer' 
                    : 'opacity-60 cursor-not-allowed'
            }`}
            style={{
                background: isFavorited 
                    ? '#1f2937' 
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
            }}
            onClick={() => {
                if (isAvailable && onViewDetails) {
                    onViewDetails(id);
                }
            }}
        >
            {/* Highlight Badge */}
            <div className="absolute top-2 md:top-3 left-2 md:left-3 z-20">
                <div className="px-2 md:px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse border" 
                    style={{
                        background: `linear-gradient(to right, ${theme.colors.featuredBadgeFrom}, ${theme.colors.featuredBadgeTo})`,
                        color: theme.colors.featuredBadgeText,
                        borderColor: theme.colors.featuredBadgeTo
                    }}>
                    ⭐ FEATURED
                </div>
            </div>

            
            {/* Badge Ribbon */}
            {rest?.badge && (
                <div className="absolute top-0 right-0 z-30 w-28 h-20 overflow-hidden ">
                    <div 
                    style={{
                        fontSize : "12px",
                        top:"20%",
                        left:"25%",
                        textAlign : "center",
                        width : "100%",
                        backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg
                        
                    }}
                    className="absolute -top-2 -right-2 text-white text-[4px] font-bold px-1 py-1 shadow-lg transform rotate-45">
                        {rest.badge}
                    </div>
            </div>
            )}
            
            {/* Availability Message for Highlighted Card */}
            {!isAvailable && (
                <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-2 px-3 text-sm font-bold z-30 rounded-t-xl md:rounded-t-2xl">
                    {!isStatusActive ? 'Currently Unavailable' : 'Not Available Now'}
                    {isStatusActive && nextAvailableTime && (
                        <div className="text-xs opacity-90 mt-1">
                            Available at {formatTimeForDisplay(nextAvailableTime)}
                        </div>
                    )}
                </div>
            )}
            
            {/* Main Content Area */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-4 lg:gap-6 p-3 md:p-4 lg:p-6 flex-1">
                {/* Product Image */}
                <div className="flex-shrink-0 relative">
                    {/* Favorites Button - bottom-left of image (mobile smaller) */}
                    <button
                        onClick={handleFavoriteToggle}
                        className="absolute bottom-2 left-2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
                        style={{
                            backgroundColor: isFavorited ? theme.colors.primary : 'rgba(255, 255, 255, 0.9)',
                            color: isFavorited ? 'white' : theme.colors.primary,
                            border: `2px solid ${isFavorited ? theme.colors.primary : 'rgba(0, 0, 0, 0.1)'}`
                        }}
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                        {isFavorited ? <HiHeart className="text-lg md:text-xl" /> : <HiOutlineHeart className="text-lg md:text-xl" />}
                    </button>
                    
                    <img 
                        className="w-48 h-44 sm:w-52 sm:h-48 md:w-60 md:h-56 lg:w-68 lg:h-64 xl:w-80 xl:h-76 object-cover rounded-xl md:rounded-2xl shadow-lg md:shadow-xl transform transition duration-300 hover:scale-105 cursor-pointer" 
                        src={`${image}`}
                        alt={name}
                        onClick={handleImageModalOpen}
                    />
                    
                    {/* Location Icon - Only show if map_url exists */}
                    {rest?.map_url && (
                        <button
                            onClick={handleMapOpen}
                            className="absolute bottom-1 right-1 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors duration-200 z-10"
                            title="Open Map Location"
                        >
                            <FaMapMarkerAlt 
                                className="w-4 h-4" 
                                style={{ color: theme.colors.primary }}
                            />
                        </button>
                    )}
                    
                    {/* Seasonal Badge */}
                    {rest?.seasonal && (
                        <div style={{
                            position: 'absolute',
                            bottom: '-1px',
                            right: '-1px',
                            background: 'linear-gradient(to right, #f97316, #dc2626)',
                            color: 'white',
                            fontSize: '7px',
                            fontWeight: 'bold',
                            padding: '3px 6px',
                            borderRadius: '12px 0px 0px 0px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            zIndex: 10
                        }}>
                            SEASONAL
                        </div>
                    )}
                </div>

                {/* Product Details */}
                <div className="flex-1 text-center md:text-left flex flex-col justify-between">
                    {/* Top Content Container */}
                    <div className="flex flex-col flex-1">
                        {/* Product Name - Top of Content */}
                        <div className="mb-1 md:mb-2 lg:mb-3">
                            <h1 className="text-gray-900 poppins text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                                {name}
                            </h1>
                        </div>
                    
                    {/* Price - Below Product Name - Only show if no Payment Type specification */}
                    {!finalHasVariants && !rest?.ProductSpecifications?.some(spec => spec.key === "Payment Type") && (
                        <div className="mb-2 md:mb-3 lg:mb-4">
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {rest?.offerPrice && rest?.offerPrice !== null ? (
                                    <div className="flex flex-col items-center md:items-start">
                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {(parseFloat(rest.offerPrice) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                        <span className="text-gray-500 line-through text-sm md:text-base lg:text-lg">{currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                    </div>
                                ) : (
                                    <span>{currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2).replace('.00', '')}</span>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Payment Type - Only show if exists */}
                    {rest?.ProductSpecifications && Array.isArray(rest.ProductSpecifications) && rest.ProductSpecifications.some(spec => spec.key === "Payment Type") && (
                        <div className="mb-2 md:mb-3 lg:mb-4">
                            {rest.ProductSpecifications.map((spec) => {
                                if (spec.key === "Payment Type") {
                                    const displayPrice = parseFloat(price).toFixed(2).replace('.00', '');
                                    return (
                                        <div key={spec.id} className="mb-2 md:mb-3 lg:mb-4">
                                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                                {rest?.offerPrice && rest?.offerPrice !== null ? (
                                                    <div className="flex flex-col items-center md:items-start">
                                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {parseFloat(rest.offerPrice).toFixed(2).replace('.00', '')}/{spec.value}</span>
                                                        <span className="text-gray-500 line-through text-sm md:text-base lg:text-lg">{currency} {displayPrice}/{spec.value}</span>
                                                    </div>
                                                ) : (
                                                    <span>{currency} {displayPrice}/{spec.value}</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                    
                    {/* Available From - Below Price */}
                    {rest?.availability && (
                        <div className="mb-2 md:mb-3 lg:mb-4">
                            <div className="inline-flex items-center rounded-lg px-3 py-1" 
                                 style={{ 
                                     backgroundColor: '#f1f5f9',
                                     border: `1px solid #e2e8f0`
                                 }}>
                                <p className="text-xs font-semibold whitespace-nowrap" 
                                   style={{ color: '#64748b' }}>
                                    From: {formatAvailabilityDate(rest.availability)}
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Features Section - Show first 5 features */}
                    {rest?.ProductFeatures && Array.isArray(rest.ProductFeatures) && rest.ProductFeatures.length > 0 && (
                        <div className="mb-2 md:mb-3 lg:mb-4">
                            <div className="text-gray-600 text-sm md:text-base">
                                {rest.ProductFeatures.slice(0, 5).map((feature, index) => (
                                    <span key={feature.id || index}>
                                        {feature.name || feature.feature || feature}
                                        {index < Math.min(rest.ProductFeatures.length, 5) - 1 && ' | '}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Specifications Section - Bottom of Text Area */}
                    <div className="mt-auto pt-2 md:pt-3 lg:pt-4">
                        {/* Location */}
                        {rest?.warranty && (
                            <div className="flex items-center gap-2 mb-1">
                                <FaMapMarkerAlt 
                                    className="w-5 h-5" 
                                    style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                />
                                <span className="text-gray-500 text-xs">{rest.warranty}</span>
                            </div>
                        )}
                        
                        {/* Property Specifications */}
                        {(rest?.ProductSpecifications && Array.isArray(rest.ProductSpecifications) && 
                          (rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                           rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") || 
                           rest.ProductSpecifications.find(spec => spec.key === "Property Size"))) && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Bedrooms */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && (
                                    <div className="flex items-center gap-1" title="Bedrooms">
                                        <FaBed 
                                            className="w-5 h-5" 
                                            style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                            title="Bedrooms"
                                        />
                                        <span 
                                            className="text-gray-500 text-xs"
                                            title="Bedrooms"
                                        >
                                            {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms")?.value}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Bathrooms */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") && (
                                    <>
                                        {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && <span className="text-gray-400 text-xs">|</span>}
                                        <div className="flex items-center gap-1" title="Bathrooms">
                                            <FaBath 
                                                className="w-5 h-5" 
                                                style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                title="Bathrooms"
                                            />
                                            <span 
                                                className="text-gray-500 text-xs"
                                                title="Bathrooms"
                                            >
                                                {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")?.value}
                                            </span>
                                        </div>
                                    </>
                                )}
                                
                                {/* Square Feet */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Property Size") && (
                                    <>
                                        {(rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                                          rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")) && <span className="text-gray-400 text-xs">|</span>}
                                        <div className="flex items-center gap-1" title="Square Feet">
                                            <FaRulerCombined 
                                                className="w-5 h-5" 
                                                style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                title="Square Feet"
                                            />
                                            <span 
                                                className="text-gray-500 text-xs"
                                                title="Square Feet"
                                            >
                                                {rest.ProductSpecifications.find(spec => spec.key === "Property Size")?.value}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {description && (
                        <p className="text-gray-600 text-xs sm:text-sm md:text-base mb-2 md:mb-3 lg:mb-4 leading-relaxed">
                            {description}
                        </p>
                    )}

                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="space-y-2 md:space-y-3 mb-2 md:mb-3 lg:mb-4">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm md:shadow-md">
                                        <div className="flex-1">
                                            <div className="text-xs sm:text-sm md:text-base font-semibold text-gray-800">{variant.name}</div>
                                            <div className="text-xs sm:text-sm md:text-base font-bold" style={{ color: theme.colors.variantPriceText }}>
                                                {hasOfferPrice(variant) ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                        <span className="text-gray-500 line-through text-xs">{currency} {formatPrice(variant.price)}</span>
                                                    </div>
                                                ) : (
                                                    <span>{currency} {formatPrice(variant.price)}</span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty > 0 && (
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm border border-gray-200 ${
                                                            isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 min-w-[20px] sm:min-w-[25px] md:min-w-[30px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm ${
                                                            isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                        style={isAvailable ? {
                                                            backgroundColor: theme.colors.productAddButtonBg,
                                                            color: theme.colors.productAddButtonText,
                                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                        } : {}}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center md:text-left mb-2 md:mb-3 lg:mb-4">
                            <div className="text-xs sm:text-sm md:text-base text-gray-600 mb-1">Total: {getTotalQuantity()} items</div>
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {formatPrice(getTotalPrice())}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="text-center md:text-left">
                            {showAddToCart && (
                                variantQuantities['base'] > 0 && (
                                    <div className="flex items-center justify-center md:justify-start space-x-3 md:space-x-4 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-md md:shadow-lg">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            disabled={!isAvailable}
                                            className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-lg md:text-xl font-bold shadow-sm border border-gray-200 ${
                                                isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                        >
                                            -
                                        </button>
                                        <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 min-w-[30px] sm:min-w-[35px] md:min-w-[40px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            disabled={!isAvailable}
                                            className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all duration-200 text-lg md:text-xl font-bold shadow-sm ${
                                                isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                            style={isAvailable ? {
                                                backgroundColor: theme.colors.productAddButtonBg,
                                                color: theme.colors.productAddButtonText,
                                                border: `1px solid ${theme.colors.productAddButtonBorder}`
                                            } : {}}
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    </div>
                    
                    {/* Location and Specifications - Bottom of Text Area */}
                    <div className="pt-2 md:pt-3">
                        {/* Location */}
                        {rest?.warranty && (
                            <div className="flex items-center gap-2 mb-1">
                                <FaMapMarkerAlt 
                                    className="w-4 h-4" 
                                    style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                />
                                <span className="text-gray-500 text-[10px]">{rest.warranty}</span>
                            </div>
                        )}
                        
                        {/* Property Specifications */}
                        {(rest?.ProductSpecifications && Array.isArray(rest.ProductSpecifications) && 
                          (rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                           rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") || 
                           rest.ProductSpecifications.find(spec => spec.key === "Property Size"))) && (
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Bedrooms */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && (
                                    <div className="flex items-center gap-1" title="Bedrooms">
                                        <FaBed 
                                            className="w-4 h-4" 
                                            style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                            title="Bedrooms"
                                        />
                                        <span 
                                            className="text-gray-500 text-[10px]"
                                            title="Bedrooms"
                                        >
                                            {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms")?.value}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Bathrooms */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms") && (
                                    <>
                                        {rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") && <span className="text-gray-400 text-[10px]">|</span>}
                                        <div className="flex items-center gap-1" title="Bathrooms">
                                            <FaBath 
                                                className="w-4 h-4" 
                                                style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                title="Bathrooms"
                                            />
                                            <span 
                                                className="text-gray-500 text-[10px]"
                                                title="Bathrooms"
                                            >
                                                {rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")?.value}
                                            </span>
                                        </div>
                                    </>
                                )}
                                
                                {/* Square Feet */}
                                {rest.ProductSpecifications.find(spec => spec.key === "Property Size") && (
                                    <>
                                        {(rest.ProductSpecifications.find(spec => spec.key === "Bedrooms") || 
                                          rest.ProductSpecifications.find(spec => spec.key === "Bathrooms")) && <span className="text-gray-400 text-[10px]">|</span>}
                                        <div className="flex items-center gap-1" title="Square Feet">
                                            <FaRulerCombined 
                                                className="w-4 h-4" 
                                                style={{ color: theme.colors.textSecondary || '#9CA3AF' }}
                                                title="Square Feet"
                                            />
                                            <span 
                                                className="text-gray-500 text-[10px]"
                                                title="Square Feet"
                                            >
                                                {rest.ProductSpecifications.find(spec => spec.key === "Property Size")?.value}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Favorites Button - Right Side of Card */}
            <button
                onClick={handleFavoriteToggle}
                className="absolute top-4 right-4 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
                style={{
                    backgroundColor: isFavorited ? '#ec4899' : 'rgba(255, 255, 255, 0.9)',
                    color: isFavorited ? 'white' : theme.colors.primary,
                    border: `2px solid ${isFavorited ? '#ec4899' : 'rgba(0, 0, 0, 0.1)'}`
                }}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
                {isFavorited ? <HiHeart className="text-lg md:text-xl" /> : <HiOutlineHeart className="text-lg md:text-xl" />}
            </button>
            
            {/* Contact Information - Full Width Footer at Bottom */}
            <div className="w-full bg-gray-50 rounded-b-xl md:rounded-b-2xl p-3 md:p-4 mt-auto">
                <ContactInfo 
                    profile={profile} 
                    isHighlighted={true} 
                    productDetails={{
                        name,
                        price,
                        currency,
                        description,
                        categoryName,
                        subcategoryName,
                        brand: rest?.brand,
                        model: rest?.model,
                        ProductSpecifications: rest?.ProductSpecifications,
                        warranty: rest?.warranty
                    }}
                />
            </div>
        </div>
        
        {/* Image Modal */}
        {showImageModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-1" onClick={handleImageModalClose}>
                <div className="relative w-full bg-white rounded-lg overflow-hidden shadow-2xl" style={{ height: '80vh', maxWidth: '95vw', marginTop: '8vh', marginBottom: '12vh' }} onClick={(e) => e.stopPropagation()}>
                    <button 
                        onClick={handleImageModalClose}
                        className="absolute top-4 right-4 z-10 bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-600 transition-all duration-200 shadow-lg border-2 border-white"
                        style={{ fontSize: '20px', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                    
                    {/* Navigation buttons - only show if multiple images */}
                    {productImages.length > 1 && (
                        <>
                            <button 
                                onClick={handlePreviousImage}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                            >
                                ‹
                            </button>
                            <button 
                                onClick={handleNextImage}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                            >
                                ›
                            </button>
                        </>
                    )}
                    
                    <img 
                        src={productImages[currentImageIndex]} 
                        alt={`${name} ${currentImageIndex + 1} of ${productImages.length}`}
                        className="w-full h-full object-contain"
                    />
                    
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                        <h3 className="text-white text-lg font-semibold">{name}</h3>
                    </div>
                    
                    {/* Image dots indicator - only show if multiple images */}
                    {productImages.length > 1 && (
                        <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-2 p-4">
                            {productImages.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                        index === currentImageIndex 
                                            ? 'bg-gray-800 scale-125' 
                                            : 'bg-gray-400 hover:bg-gray-600'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}
        </>
    )
}

export default FoodItem
